import { Box, Typography, Stack, StackProps, ButtonGroup, Button, Grid } from '@mui/material';
import DateInfoTooltip, { DateInfoTooltipProps } from 'components/Tooltip/DateInfoTooltip';
import { ReactComponent as ToolTipIcon } from '../../../assets/PepWise2.0/InfoIcon.svg';
import { ReactComponent as MoreBlackIcon } from '../../../assets/PepWise2.0/more-vertical-black.svg';
import { ReactComponent as PlusIcon } from '../../../assets/plus-icon.svg';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import WidgetMoreActionMenu, {
  MoreOptionMenuProps,
} from 'components/Dropdown/WidgetMoreActionMenu';

type SLAItemType = {
  label: string;
  count: number;
  onBtnClick: (event?: any) => void;
};

type ProgramCardProps = {
  title: string;
  isEditEnabled?: boolean;
  programId: string;
  projectCount: number;
  projectSLAs?: SLAItemType[];
  statusCount?: number;
  bgColor?: string;

  minTitleHeight?: `${number}px`;
  hasTooltip?: boolean;

  infoTooltip?: Omit<DateInfoTooltipProps, 'children'>;
  infoOptionMenu?: MoreOptionMenuProps;

  onClickAddProject?: (event: any) => void;

  handleEvent?: () => void;
  ratioNumber?: {
    number: number | string;
    totalNumber: number | string;
  };
  containerProps?: StackProps;
};

const ProgramCard = ({
  bgColor,
  title = '',
  programId,
  projectCount,
  projectSLAs,
  statusCount,
  minTitleHeight = '45px',
  infoTooltip,
  infoOptionMenu,
  isEditEnabled,
  handleEvent,
  onClickAddProject,
  containerProps,
}: Readonly<ProgramCardProps>) => {
  const theme = useTheme();
  const colors = theme.palette;

  const resolvedBgColor = bgColor ?? colors.widget.background.card;

  const getSLAClassName = (label: string): string => {
    if (label === 'SLA Missed') {
      return 'color-missed';
    }

    if (label === 'SLA Met') {
      return 'color-met';
    }

    if (label === 'In Progress') {
      return 'color-progress';
    }

    if (label === 'Scheduled') {
      return 'color-scheduled';
    }

    return 'color-others';
  };
  return (
    <Box
      display="flex"
      flexGrow={1}
      height="100%"
      width="100%"
      position="relative"
      {...(handleEvent && { onClick: handleEvent, sx: { cursor: 'pointer' } })}
    >
      <Stack
        minHeight="100px"
        p={2}
        bgcolor={resolvedBgColor}
        fontFamily="Montserrat"
        borderRadius="5px"
        flexGrow={1}
        width="90%"
        {...containerProps}
        sx={{
          position: 'relative',
          '&:hover .hover-content': {
            opacity: 1,
          },
          ...containerProps?.sx,
        }}
      >
        <Stack minHeight={minTitleHeight}>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <Typography
                variant="widgetTitle"
                sx={{
                  width: '100%',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  '& a': { color: colors.widget.text.title },
                }}
              >
                <Link
                  title={title}
                  to={''}
                  style={{
                    color: colors.widget.text.title,
                    textDecoration: 'none',
                    transition: 'color 0.3s ease',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = `${colors.widget.text.action.primary}`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = `${colors.widget.text.title}`)
                  }
                >
                  {title}
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                {!!infoTooltip && (
                  <DateInfoTooltip {...infoTooltip}>
                    <ToolTipIcon />
                  </DateInfoTooltip>
                )}
                {!!infoOptionMenu && isEditEnabled && (
                  <WidgetMoreActionMenu {...infoOptionMenu}>
                    <MoreBlackIcon />
                  </WidgetMoreActionMenu>
                )}
              </Box>
            </Grid>
          </Grid>
        </Stack>
        <Stack minHeight="20px" flexGrow={1} justifyContent="center">
          {projectCount > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant="widgetBodyLabel"
                  mb={2}
                  sx={{ color: colors.widget.text.value }}
                >
                  Projects SLAs
                </Typography>
                <Typography
                  variant="widgetBodyLabelStrong"
                  mb={2}
                  sx={{ color: colors.widget.text.value }}
                >
                  Total Projects {projectCount}
                </Typography>
              </Box>
              <ButtonGroup
                sx={{
                  boxShadow: 'none',
                  '& button': {
                    flex: 1,
                    variant: 'pepwiseButtonText',
                    borderRadius: '4px',
                    color: colors.widget.sla.text,
                    padding: '5px',
                    overflow: 'hidden',
                    height: '32px',
                    // width: '100px',
                    textTransform: 'uppercase',
                    transitionProperty: 'width, opacity',
                    transitionDuration: '0.3s',
                    transitionTimingFunction: 'ease-out, ease-in',
                    transitionDelay: '0s, 0.05s',
                  },
                  '& .toggle-label': {
                    width: 0,
                    marginLeft: '8px',
                    opacity: 0,
                  },
                  // On hover: shrink non-hover buttons more
                  '&:hover button': {
                    flex: '1 1 0',
                  },
                  '& button:hover': {
                    width: '150px',
                    flex: '2 1 0',
                  },
                  '& button:hover .toggle-label': {
                    width: 'auto',
                    transitionProperty: 'width, opacity',
                    transitionDuration: '0.3s',
                    transitionTimingFunction: 'ease-out, ease-in',
                    transitionDelay: '0s, 0.1s',
                    opacity: 1,
                  },
                  '& .color-missed': {
                    backgroundColor: colors.widget.sla.missed.bg,
                    '&:hover': {
                      backgroundColor: colors.widget.sla.missed.hoverBg,
                    },
                  },
                  '& .color-met': {
                    backgroundColor: colors.widget.sla.met.bg,
                    '&:hover': {
                      backgroundColor: colors.widget.sla.met.hoverBg,
                    },
                  },
                  '& .color-scheduled': {
                    backgroundColor: colors.widget.sla.scheduled.bg,
                    '&:hover': {
                      backgroundColor: colors.widget.sla.scheduled.hoverBg,
                    },
                  },
                  '& .color-others': {
                    backgroundColor: colors.widget.sla.others.bg,
                    '&:hover': {
                      backgroundColor: colors.widget.sla.others.hoverBg,
                    },
                  },
                  '& .color-progress': {
                    backgroundColor: colors.widget.sla.progress.bg,
                    '&:hover': {
                      backgroundColor: colors.widget.sla.progress.hoverBg,
                    },
                  },
                }}
              >
                {projectSLAs?.map(
                  ({ label, count, onBtnClick }) =>
                    count && (
                      <Button
                        variant="pepwiseButtonText"
                        className={getSLAClassName(label)}
                        key={label}
                        onClick={(event) => {
                          onBtnClick('menuItem');
                        }}
                      >
                        {count} <span className="toggle-label">{label}</span>
                      </Button>
                    ),
                )}
              </ButtonGroup>
              {!statusCount ? (
                <Typography variant="tableColumnFont" sx={{ color: colors.widget.text.noChange }}>
                  No Data
                </Typography>
              ) : (
                ''
              )}
            </>
          )}

          {isEditEnabled && projectCount == 0 && (
            <Button
              variant="pepwiseButtonText"
              startIcon={<PlusIcon />}
              sx={{
                alignSelf: 'flex-start',
                pl: 0, // remove internal left padding
                pr: 2,
                minWidth: 'auto',
                borderRadius: '8px',
                color: colors.widget.text.action.primary,
                '& .MuiButton-startIcon': {
                  marginRight: '6px',
                  marginLeft: 0, // remove default left shift
                },
                '& svg path': {
                  fill: colors.widget.text.action.primary,
                },
              }}
              onClick={onClickAddProject}
            >
              ADD PROJECT
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default ProgramCard;
